import React, { useEffect, useState } from 'react';
import axios from 'axios';
import FlightSearchForm from './FlightSearchForm';
import airports from '../data/airports.json';
import airlines from '../data/airlines.json';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertTriangle, Globe, ArrowRight, Globe2, Info, MessageCircle, X, CloudSun, MapPin } from 'lucide-react';
import PopularDestinations from '../components/PopularDestinations';
import Temoignages from '../components/Temoignages';
import AutresDestinations from '../components/AutresDestinations';
import Footer from '../components/Footer';
import PassengerForm from './PassengerForm';
import ReservationSheet from './ReservationSheet';
import MapAeroports from '../components/MapAeroports';
import NewsList from '../components/NewsList';
import CountryInfo from '../components/CountryInfo';
import AITravelAssistant from '../ia/AITravelAssistant';
import AIFlightAssistant from '../ia/AIFlightAssistant';

function Home() {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [reservationData, setReservationData] = useState(null);

  const [travelInfo, setTravelInfo] = useState(null);
  const [news, setNews] = useState([]);
  const [showPassengerForm, setShowPassengerForm] = useState(false);


  const [showChat, setShowChat] = useState(false);



  // --- Charger infos pays + news quand un vol est sélectionné ---
  useEffect(() => {
    if (selectedFlight && !showPassengerForm) {
      const lastItinerary = selectedFlight.itineraries[selectedFlight.itineraries.length - 1];
      const arrival = lastItinerary.segments[lastItinerary.segments.length - 1];
      const airport = getAirportData(arrival.arrival.iataCode);

      if (airport?.iso_country) {
        getTravelInfo(airport.iso_country);
        getCountryNews(airport.country);
      }

      const timer = setTimeout(() => {
        if (!travelInfo && news.length === 0) {
          setShowPassengerForm(true);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [selectedFlight, showPassengerForm, travelInfo, news.length]);





  const aircraftTypes = {
    "318": "Airbus A318", "319": "Airbus A319", "320": "Airbus A320", "321": "Airbus A321", "32N": "Airbus A320neo", "32Q": "Airbus A321neo",
    "330": "Airbus A330", "332": "Airbus A330-200", "333": "Airbus A330-300", "338": "Airbus A330-800neo", "339": "Airbus A330-900neo",
    "342": "Airbus A340-200", "343": "Airbus A340-300", "345": "Airbus A340-500", "346": "Airbus A340-600", "350": "Airbus A350",
    "351": "Airbus A350-900", "359": "Airbus A350-900", "35K": "Airbus A350-1000", "388": "Airbus A380-800",
    "717": "Boeing 717", "727": "Boeing 727", "732": "Boeing 737-200", "733": "Boeing 737-300", "734": "Boeing 737-400",
    "735": "Boeing 737-500", "736": "Boeing 737-600", "737": "Boeing 737-700", "738": "Boeing 737-800", "739": "Boeing 737-900",
    "73H": "Boeing 737-800 (winglets)", "73J": "Boeing 737 MAX 9", "73M": "Boeing 737 MAX 8", "752": "Boeing 757-200",
    "753": "Boeing 757-300", "762": "Boeing 767-200", "763": "Boeing 767-300", "764": "Boeing 767-400ER", "772": "Boeing 777-200",
    "773": "Boeing 777-300", "77L": "Boeing 777-200LR", "77W": "Boeing 777-300ER", "778": "Boeing 777-8", "779": "Boeing 777-9",
    "787": "Boeing 787 Dreamliner", "788": "Boeing 787-8 Dreamliner", "789": "Boeing 787-9 Dreamliner", "78X": "Boeing 787-10 Dreamliner",
    "E70": "Embraer 170", "E75": "Embraer 175", "E90": "Embraer 190", "E95": "Embraer 195", "E2S": "Embraer E190-E2", "E2L": "Embraer E195-E2",
    "CRJ": "Bombardier CRJ", "CR1": "Bombardier CRJ100", "CR2": "Bombardier CRJ200", "CR7": "Bombardier CRJ700", "CR9": "Bombardier CRJ900",
    "CRA": "Bombardier CRJ1000", "MRJ": "Mitsubishi SpaceJet", "AT4": "ATR 42", "AT5": "ATR 42-500", "AT7": "ATR 72", "ATR": "ATR 72-600",
    "F70": "Fokker 70", "F100": "Fokker 100", "DC3": "Douglas DC-3", "DC9": "Douglas DC-9", "MD8": "McDonnell Douglas MD-80",
    "MD9": "McDonnell Douglas MD-90", "MD1": "McDonnell Douglas MD-11",
  };

  const getIcaoFromIata = (iata) => {
    if (!airlines?.data) return null;
    const found = airlines.data.find(a => a.iata_code === iata);
    return found?.icao_code || null;
  };

  const getCompagnyName = (iata) => {
    if (!airlines?.data) return null;
    const found = airlines.data.find(a => a.iata_code === iata);
    return found?.name || null;
  };




  // Infos administratives via proxy backend (évite CORS)
  const getTravelInfo = async (countryCode) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/external_apis/proxy.php`,
        {
          params: {
            type: 'country',
            param: countryCode
          }
        }
      );
      if (res.data && res.data[0]) {
        setTravelInfo(res.data[0]);
      }
    } catch (e) {
      console.error("Erreur travel info:", e);
    }
  };





  const getCountryNews = async (countryCode) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/external_apis/proxy.php`,
        {
          params: {
            type: 'news',
            param: countryCode
          }
        }
      );
      setNews(res.data.articles || []);
    } catch (e) {
      console.error("Erreur news:", e);
    }
  };




  // --- Fonction météo via proxy backend ---
  const getWeatherForecast = async (lat, lon, datetime) => {
    try {
      const date = new Date(datetime);
      const isoDate = date.toISOString().split("T")[0];
      const hour = date.getHours();

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/external_apis/proxy.php`,
        {
          params: {
            type: 'weather',
            lat: lat,
            lon: lon,
            date: isoDate
          }
        }
      );

      if (res.data?.hourly) {
        const index = res.data.hourly.time.findIndex((t) => t.includes(`T${String(hour).padStart(2, "0")}:00`));
        if (index !== -1) {
          return {
            temperature: res.data.hourly.temperature_2m[index],
            code: res.data.hourly.weathercode[index],
          };
        }
      }
    } catch (e) {
      console.error("Erreur météo :", e);
    }
    return null;
  };

  // --- Mapping simple Open-Meteo weather codes ---
  const weatherIcons = {
    0: "☀️", 1: "🌤️", 2: "⛅", 3: "☁️",
    45: "🌫️", 51: "🌦️", 61: "🌧️", 71: "❄️", 95: "⛈️",
  };





  function getItineraryType(flight) {
    try {
      const firstItinerary = flight.itineraries[0];
      const firstSegment = firstItinerary.segments[0];
      const lastSegment = firstItinerary.segments[firstItinerary.segments.length - 1];

      const depAirport = getAirportData(firstSegment.departure.iataCode);
      const arrAirport = getAirportData(lastSegment.arrival.iataCode);

      const depCountry = depAirport?.iso_country || null;
      const arrCountry = arrAirport?.iso_country || null;

      if (!depCountry || !arrCountry) return "inconnu";

      if (depCountry === "CI" && arrCountry === "CI") {
        return "national";
      }

      return "international";
    } catch (e) {
      console.error("Erreur getItineraryType:", e);
      return "inconnu";
    }
  }








  const getAdjustedPrice = (flight) => {
    try {
      const firstItinerary = flight.itineraries[0];
      const firstSegment = firstItinerary.segments[0];
      const lastSegment = firstItinerary.segments[firstItinerary.segments.length - 1];

      const depAirport = getAirportData(firstSegment.departure.iataCode);
      const arrAirport = getAirportData(lastSegment.arrival.iataCode);

      let basePrice = parseFloat(flight.price?.total ?? flight.price ?? 0);
      if (isNaN(basePrice)) basePrice = 0;

      const depCountry = depAirport?.iso_country ?? null;
      const arrCountry = arrAirport?.iso_country ?? null;

      if (!depCountry || !arrCountry) {
        return Math.round(basePrice);
      }

      const totalPassengers = (flight.travelerPricings?.length) || 1;
      const commissionPerTicket = (depCountry === "CI" && arrCountry === "CI") ? 20000 : 50000;
      basePrice += commissionPerTicket * totalPassengers;

      return Math.round(basePrice);
    } catch (e) {
      console.error("getAdjustedPrice error:", e);
      return Math.round(parseFloat(flight.price?.total ?? 0) || 0);
    }
  };



  const handleSearch = async (searchParams) => {
    setLoading(true);
    setErreur(null);
    setFlights([]);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/backend_amadeus/searchFlights.php`,
        {
          params: {
            ...searchParams,
            max: 10,
          },
        }
      );

      if (res.data.data?.length) {
        setFlights(res.data.data);
      } else {
        setErreur('Aucune offre trouvée.');
      }
    } catch (error) {
      console.error(error);
      setErreur("Erreur lors de la récupération des vols.");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime) =>
    new Date(datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const formatDate = (datetime) =>
    new Date(datetime).toLocaleDateString();

  const getAirportData = (iata) =>
    airports.find((a) => a.iata_code === iata);

  const renderAirlineLogos = (carrierCodes) =>
    carrierCodes.map((iata, idx) => {
      const icao = getIcaoFromIata(iata);
      const logoPath = icao ? `./images/${icao}.png` : '/data/images/default.png';
      return (
        <img
          key={iata + '-' + idx}
          src={logoPath}
          alt={iata}
          title={iata}
          className="inline-block w-auto h-auto mr-2 border shadow"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    });



  const AirportWeather = ({ iata, datetime }) => {
    const airport = getAirportData(iata);
    const [weather, setWeather] = useState(null);

    useEffect(() => {
      if (airport?.latitude && airport?.longitude && datetime) {
        getWeatherForecast(airport.latitude, airport.longitude, datetime).then(setWeather);
      }
    }, [iata, datetime, airport?.latitude, airport?.longitude]);

    if (!weather) return null;

    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <CloudSun className="w-3 h-3 text-blue-400" />
        <span>{weatherIcons[weather.code] || "🌍"} {weather.temperature}°C</span>
      </span>
    );
  };



  const renderItinerary = (itinerary, type) => {
    const segments = itinerary.segments;
    const departure = segments[0];
    const arrival = segments[segments.length - 1];
    const stops = segments.length - 1;
    const airlineCodes = [...new Set(segments.map((s) => s.carrierCode))];
    const durationFormatted = itinerary.duration.replace("PT", "").toLowerCase();
    const depTime = formatTime(departure.departure.at);
    const arrTime = formatTime(arrival.arrival.at);
    const depDate = formatDate(departure.departure.at);

    const depMinutes = new Date(departure.departure.at).getHours() * 60 + new Date(departure.departure.at).getMinutes();
    const arrMinutes = new Date(arrival.arrival.at).getHours() * 60 + new Date(arrival.arrival.at).getMinutes();
    const travelMinutes = arrMinutes > depMinutes ? arrMinutes - depMinutes : arrMinutes + 1440 - depMinutes;
    const timelineWidth = Math.min(100, Math.max(30, (travelMinutes / 1440) * 100));

    const depAirport = getAirportData(departure.departure.iataCode);
    const arrAirport = getAirportData(arrival.arrival.iataCode);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`rounded-2xl p-3 sm:p-5 mb-3 sm:mb-4 border shadow-sm hover:shadow-md transition-shadow ${
          type === 'retour' 
            ? 'bg-gradient-to-br from-amber-50/80 to-white border-amber-200' 
            : 'bg-gradient-to-br from-blue-50/80 to-white border-blue-200'
        }`}
      >
        {/* En-tête : type de vol + date */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${type === 'retour' ? 'bg-amber-100' : 'bg-blue-100'}`}>
              {type === 'retour' 
                ? <Globe className="text-amber-500 w-4 h-4" /> 
                : <Globe className="text-blue-500 w-4 h-4" />
              }
            </div>
            <span className={`text-sm font-bold ${type === 'retour' ? 'text-amber-700' : 'text-blue-700'}`}>
              {type === 'retour' ? '🔄 Retour' : '🛫 Aller'}
            </span>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{depDate}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="font-medium">{durationFormatted}</span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span>{stops === 0 ? 'Direct' : `${stops} escale${stops > 1 ? 's' : ''}`}</span>
          </div>
        </div>

        {/* Timeline de vol moderne */}
        <div className="relative">
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Départ */}
            <div className="flex flex-col items-center shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shadow-md ring-2 ring-blue-200">
                {depTime}
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-gray-700 mt-1 uppercase">{departure.departure.iataCode}</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 truncate max-w-[60px] sm:max-w-[80px] text-center">
                {depAirport?.city || ''}
              </span>
            </div>

            {/* Barre de progression */}
            <div className="flex-1 relative h-0.5 bg-gray-200 rounded-full min-w-[40px]">
              <div 
                className={`absolute top-0 left-0 h-full rounded-full ${type === 'retour' ? 'bg-amber-400' : 'bg-blue-400'}`}
                style={{ width: `${timelineWidth}%` }}
              />
              {segments.slice(1).map((seg, idx) => (
                <div 
                  key={idx}
                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gray-300 border-2 border-white shadow"
                  style={{ left: `${(idx + 1) * (100 / segments.length)}%` }}
                  title={`Escale: ${seg.departure.iataCode}`}
                />
              ))}
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2" style={{ left: `${Math.min(timelineWidth, 90)}%` }}>
                <svg className={`w-4 h-4 ${type === 'retour' ? 'text-amber-500' : 'text-blue-500'}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                </svg>
              </div>
            </div>

            {/* Arrivée */}
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ring-2 ${
                type === 'retour' ? 'bg-amber-500 ring-amber-200' : 'bg-emerald-500 ring-emerald-200'
              }`}>
                {arrTime}
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-gray-700 mt-1 uppercase">{arrival.arrival.iataCode}</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 truncate max-w-[60px] sm:max-w-[80px] text-center">
                {arrAirport?.city || ''}
              </span>
            </div>
          </div>
        </div>

        {/* Infos aéroports : météo + carte compacte */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {/* Aéroport départ */}
          <div className="bg-white/60 rounded-lg p-2 border border-gray-100">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase mb-1">
              <MapPin className="w-2.5 h-2.5" />
              {departure.departure.iataCode}
            </div>
            <p className="text-[10px] text-gray-600 leading-tight mb-1">{depAirport?.name || ''}</p>
            <div className="flex items-center gap-2">
              <AirportWeather iata={departure.departure.iataCode} datetime={departure.departure.at} />
            </div>
            {depAirport && (
              <div className="mt-1">
                <MapAeroports aeroports={depAirport} />
              </div>
            )}
          </div>

          {/* Aéroport arrivée */}
          <div className="bg-white/60 rounded-lg p-2 border border-gray-100">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase mb-1">
              <MapPin className="w-2.5 h-2.5" />
              {arrival.arrival.iataCode}
            </div>
            <p className="text-[10px] text-gray-600 leading-tight mb-1">{arrAirport?.name || ''}</p>
            <div className="flex items-center gap-2">
              <AirportWeather iata={arrival.arrival.iataCode} datetime={arrival.arrival.at} />
            </div>
            {arrAirport && (
              <div className="mt-1">
                <MapAeroports aeroports={arrAirport} />
              </div>
            )}
          </div>
        </div>

        {/* Compagnies et logos */}
        <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-gray-500">
          {airlineCodes.map((code, idx) => {
            const name = getCompagnyName(code);
            return (
              <span key={idx} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md">
                {renderAirlineLogos([code])}
                <span className="font-medium text-gray-700">{name || code}</span>
              </span>
            );
          })}
        </div>

        {/* Détails dépliants des segments */}
        {segments.length > 1 && (
          <details className="group mt-3 border-t border-gray-100 pt-2">
            <summary className="text-xs text-blue-600 font-medium cursor-pointer hover:text-blue-800 transition flex items-center gap-1">
              <span>🛩️ Voir les {segments.length} segments</span>
              <svg className="w-3 h-3 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="mt-2 space-y-2">
              {segments.map((seg, idx) => (
                <div key={idx} className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-gray-700">{seg.carrierCode} {seg.number}</span>
                    <span className="text-gray-400">{aircraftTypes[seg.aircraft?.code] || seg.aircraft?.code || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="font-medium">{seg.departure.iataCode}</span>
                    <span>{formatTime(seg.departure.at)}</span>
                    <ArrowRight className="w-3 h-3 text-gray-300" />
                    <span className="font-medium">{seg.arrival.iataCode}</span>
                    <span>{formatTime(seg.arrival.at)}</span>
                    <span className="text-gray-400 ml-auto">{seg.duration.replace("PT", "").toLowerCase()}</span>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </motion.div>
    );
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50">
      {/* Header / Hero */}
      <header className="relative w-full h-screen overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src="./videos/video1.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-black/10" />
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 flex flex-col justify-center items-center text-center h-full px-6"
        >
          <motion.h1
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-6xl md:text-8xl font-extrabold text-white drop-shadow-2xl tracking-wide"
          >
            Djater Voyage
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-6 text-lg md:text-2xl text-gray-200 max-w-2xl leading-relaxed"
          >
            ✈️ Partez à la découverte du monde avec{" "}
            <span className="text-yellow-400 font-semibold">style et sérénité</span>.
            <br /> Réservez et vivez vos plus belles aventures.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-10 flex flex-wrap gap-6 justify-center"
          >
            <a
              href="apropos"
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-100 text-white font-bold py-3 px-8 rounded-full shadow-xl transition transform hover:scale-105"
            >
              <Info className="w-5 h-5" />
              A propos
            </a>
            <a
              href="#contact"
              className="flex items-center gap-2 bg-white hover:bg-blue-100 text-blue-700 font-bold py-3 px-8 rounded-full shadow-xl transition transform hover:scale-105"
            >
              <Globe2 className="w-5 h-5" />
              Contactez-nous
            </a>
          </motion.div>
        </motion.div>
      </header>



      {selectedFlight && !reservationData && (
        <>
          {!showPassengerForm && !travelInfo && news.length === 0 && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
                <div className="mb-6">
                  <Loader2 className="mx-auto animate-spin text-blue-600 w-12 h-12 mb-4" />
                  <p className="text-gray-700 text-lg mb-2">Chargement des informations de voyage...</p>
                  <p className="text-gray-500 text-sm">Cela peut prendre quelques secondes</p>
                </div>
                <button
                  onClick={() => setShowPassengerForm(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition flex items-center justify-center gap-2"
                >
                  <span>Continuer sans attendre</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSelectedFlight(null)}
                  className="mt-3 text-gray-500 hover:text-red-600 transition text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {!showPassengerForm && (travelInfo || news.length > 0) && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
              <div className="relative bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl p-8 animate-slideUp">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Informations voyage</h2>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setNews([]); setTravelInfo(null); setShowPassengerForm(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-full shadow transition"
                    >
                      <span>Continuer la réservation</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => { setNews([]); setTravelInfo(null); setSelectedFlight(null); }}
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-red-500 hover:text-white text-gray-600 transition"
                      aria-label="Fermer"
                    >✕</button>
                  </div>
                </div>
                <div className="space-y-6">
                  {travelInfo && <div className="p-4 bg-gray-50 rounded-xl shadow-sm"><CountryInfo country={travelInfo} /></div>}
                  {news.length > 0 && <div className="p-4 bg-gray-50 rounded-xl shadow-sm"><NewsList news={news} /></div>}
                </div>
              </div>
            </div>
          )}

          <AnimatePresence>
            {showPassengerForm && (
              <PassengerForm
                flight={selectedFlight}
                onClose={() => { setShowPassengerForm(false); setSelectedFlight(null); }}
                onReservation={(amadeusData) => setReservationData(amadeusData)}
              />
            )}
          </AnimatePresence>
        </>
      )}



      {reservationData ? (
        <ReservationSheet reservationData={reservationData} />
      ) : (
        <>
          <section id="search" className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 bg-white rounded-2xl shadow-xl mb-8 sm:mb-10 md:mb-12 border-2 border-blue-100">
            <FlightSearchForm onSearch={handleSearch} />
            <AnimatePresence>
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center mt-10">
                  <Loader2 className="mx-auto animate-spin text-blue-600 w-10 h-10" />
                  <p className="text-blue-600 mt-4 text-lg font-semibold">Recherche de vols en cours...</p>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {erreur && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="text-center mt-8 text-red-600 flex flex-col items-center">
                  <AlertTriangle className="w-8 h-8 mb-2" />
                  <p className="text-lg font-semibold">{erreur}</p>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {!loading && flights.length > 0 && (
                <>
                  <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-blue-700 mb-6 sm:mb-8"
                  >
                    ✈️ Résultats de recherche
                  </motion.h2>
                  <div className="mt-10 space-y-8">
                    {flights.map((flight, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.07 }}
                        className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                      >
                        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                              {flight.travelerPricings[0]?.fareDetailsBySegment[0]?.cabin || 'Économique'}
                            </span>
                            <span className="text-gray-300">|</span>
                            <span className="text-gray-500">
                              {flight.travelerPricings?.length || 1} passager{(flight.travelerPricings?.length || 1) > 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setFlights(prev =>
                                prev.map((f, i) => i === index ? { ...f, showBaggage: !f.showBaggage } : f)
                              )}
                              className={`text-xs font-medium px-3 py-1.5 rounded-full transition flex items-center gap-1.5 ${
                                flight.showBaggage ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              Bagages
                              <span>{flight.showBaggage ? "▲" : "▼"}</span>
                            </button>
                          </div>
                        </div>

                        <div className="px-4 sm:px-6 py-3 sm:py-4">
                          {flight.itineraries.map((itin, iIdx) =>
                            <div key={iIdx}>{renderItinerary(itin, iIdx === 1 ? 'retour' : 'aller')}</div>
                          )}
                        </div>

                        <AnimatePresence>
                          {flight.showBaggage && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 sm:px-6 pb-3">
                                <div className="bg-amber-50 rounded-xl border border-amber-200 p-3 sm:p-4 space-y-2">
                                  <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    Détail des bagages
                                  </h4>
                                  {flight.travelerPricings[0]?.fareDetailsBySegment.map((fare, idx) => {
                                    const bags = fare.includedCheckedBags;
                                    return (
                                      <div key={idx} className="text-sm bg-white rounded-lg p-2.5 border border-amber-100">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-gray-700">Segment {idx + 1}</span>
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bags?.quantity ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                            {bags?.quantity ? `${bags.quantity} bagage${bags.quantity > 1 ? 's' : ''}` : 'Pas de bagage'}
                                          </span>
                                        </div>
                                        {bags?.weight && <span className="text-xs text-gray-500 mt-1 block">Poids max : {bags.weight} {bags.weightUnit}</span>}
                                        {fare.conditions && (
                                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {fare.conditions.changeable && <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">Modifiable</span>}
                                            {fare.conditions.refundable && <span className="text-[10px] bg-green-100 text-green-600 px-1.5 py-0.5 rounded">Remboursable</span>}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-green-50/50 via-white to-green-50/50 border-t border-gray-100">
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex flex-col items-center sm:items-start">
                              <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider font-medium">Prix total TTC</span>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900">
                                  {new Intl.NumberFormat('fr-FR').format(getAdjustedPrice(flight))}
                                </span>
                                <span className="text-sm sm:text-base font-medium text-gray-500">{flight.price.currency}</span>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold py-2.5 sm:py-3 px-6 sm:px-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm sm:text-base flex items-center justify-center gap-2"
                              onClick={() => setSelectedFlight({
                                ...flight,
                                adjustedPrice: getAdjustedPrice(flight),
                                itineraryType: getItineraryType(flight)
                              })}
                            >
                              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Réserver ce vol
                            </motion.button>
                          </div>
                        </div>

                        <div className="border-t border-gray-100">
                          <AIFlightAssistant flight={flight} getAdjustedPrice={getAdjustedPrice} />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </>
              )}
            </AnimatePresence>
          </section>

          <PopularDestinations />
          <AutresDestinations />
          <Temoignages />
          <Footer />

          <button
            onClick={() => setShowChat(!showChat)}
            className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition z-50"
          >
            {showChat ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          </button>

          <AnimatePresence>
            {showChat && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.3 }}
                className="fixed bottom-20 right-6 w-96 rounded-2xl border z-50 overflow-hidden flex flex-col"
              >
                <AITravelAssistant
                  onFlightSelect={(flight) => {
                    const adjustedPrice = getAdjustedPrice(flight);
                    const itineraryType = getItineraryType(flight);
                    setSelectedFlight({ ...flight, adjustedPrice, itineraryType });
                  }}
                  onOpenSearch={() => window.scrollTo({ top: document.getElementById('search').offsetTop, behavior: 'smooth' })}
                  onOpenReservation={() => setShowPassengerForm(true)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

export default Home;